import React, { useEffect, useRef, useState } from 'react';

export interface ContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  avatar?: string | null;
  children?: ContextMenuItem[];
  onClick: () => void;
  danger?: boolean;
  separator?: boolean;
  hidden?: boolean;
}

interface ContextMenuProps {
  items: ContextMenuItem[];
  position: { x: number; y: number } | null;
  onClose: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ items, position, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState(position);
  const [openPath, setOpenPath] = useState<number[]>([]);
  const [submenuDirections, setSubmenuDirections] = useState<Record<string, 'left' | 'right'>>({});

  useEffect(() => {
    if (!position) return;

    // Adjust position to stay within viewport
    const adjust = () => {
      const menu = menuRef.current;
      if (!menu) {
        setAdjustedPos(position);
        return;
      }
      const rect = menu.getBoundingClientRect();
      let x = position.x;
      let y = position.y;
      if (x + rect.width > window.innerWidth) x = window.innerWidth - rect.width - 4;
      if (y + rect.height > window.innerHeight) y = window.innerHeight - rect.height - 4;
      if (x < 0) x = 4;
      if (y < 0) y = 4;
      setAdjustedPos({ x, y });
    };

    requestAnimationFrame(adjust);
  }, [position]);

  useEffect(() => {
    if (!position) return;
    const handleClick = () => onClose();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [position, onClose]);

  if (!position) return null;

  const visibleItems = items.filter((i) => !i.hidden);
  const resolveSubmenuDirection = (pathKey: string, rect: DOMRect) => {
    const submenuWidth = 220;
    const gap = 8;
    const canOpenRight = rect.right + submenuWidth + gap <= window.innerWidth;
    const canOpenLeft = rect.left - submenuWidth - gap >= 0;
    const direction = canOpenRight || !canOpenLeft ? 'right' : 'left';
    setSubmenuDirections((current) => (
      current[pathKey] === direction ? current : { ...current, [pathKey]: direction }
    ));
  };

  const renderItems = (menuItems: ContextMenuItem[], path: number[] = []) => {
    const visible = menuItems.filter((i) => !i.hidden);
    return visible.map((item, i) => {
      const itemPath = [...path, i];
      const pathKey = itemPath.join('-');
      const hasChildren = !!item.children?.some((child) => !child.hidden);
      const isOpen = hasChildren && itemPath.every((part, index) => openPath[index] === part);
      const submenuDirection = submenuDirections[pathKey] ?? 'right';
      return (
        <React.Fragment key={pathKey}>
          {item.separator && i > 0 && <div className="h-px bg-border-color/80 my-1" />}
          <div
            className="relative"
            onMouseEnter={(event) => {
              if (!hasChildren) return;
              resolveSubmenuDirection(pathKey, event.currentTarget.getBoundingClientRect());
              setOpenPath(itemPath);
            }}
          >
            <button
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors rounded-sm ${
                item.danger
                  ? 'text-error hover:bg-error/10'
                  : 'text-text-primary hover:bg-highlight/80'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) {
                  setOpenPath(itemPath);
                  return;
                }
                item.onClick();
                onClose();
              }}
            >
              {item.avatar ? (
                <img src={item.avatar} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
              ) : item.icon ? (
                <span className="w-4 h-4 flex-shrink-0">{item.icon}</span>
              ) : (
                <span className="w-4 flex-shrink-0" />
              )}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {hasChildren && <span className="text-text-muted text-xs">›</span>}
            </button>
            {hasChildren && isOpen && (
              <div
                className={`absolute top-0 min-w-[210px] rounded-lg border border-border-color bg-bg-secondary shadow-2xl shadow-black/40 p-1 ${
                  submenuDirection === 'left' ? 'right-full mr-1' : 'left-full ml-1'
                }`}
              >
                {renderItems(item.children || [], itemPath)}
              </div>
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[210px] bg-bg-secondary border border-border-color rounded-lg shadow-2xl shadow-black/40 p-1 animate-fade-in"
      style={{ left: adjustedPos?.x ?? position.x, top: adjustedPos?.y ?? position.y }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {renderItems(visibleItems)}
    </div>
  );
};

export default ContextMenu;
